import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Clipboard write plus the 2s "Copied" flip every copy affordance in the app
 * shows. One owner, because the same dance was written by hand in three places
 * and a surface that copies from two controls (the code block and the button
 * beside it) has to flip both from one state.
 *
 * `failed` matters as much as `copied`. A denied or unavailable clipboard is
 * silent otherwise, so a caller whose payload is not already on screen must
 * reveal it for manual copy when `failed` flips.
 *
 * try/catch, not `.then(ok, err)`: on an insecure origin `navigator.clipboard`
 * is undefined and the property access throws before a promise exists.
 */
export const useCopyState = (value: string): { copied: boolean; failed: boolean; copy: () => Promise<void> } => {
  const [copied, setCopied] = useState(false);
  const [failed, setFailed] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Copying and then navigating away would otherwise set state on an unmounted
  // component and keep the closure alive until the timer fires.
  useEffect(() => () => clearTimeout(timer.current), []);

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value);
      setFailed(false);
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
      setFailed(true);
    }
  }, [value]);

  return { copied, failed, copy };
};
